/* Unified typeahead search — pages, publishers, and documents in one box. Used both as the
 * home dashboard hero search (variant="hero") and, replacing the old publisher-only top-nav
 * search, in the nav (variant="compact"). The people list honours the same visibility rule as
 * the former UserSearch: group-view-only users see only their own group (plus any delegates). */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowReturnLeft, Search as SearchIcon, XLg } from "react-bootstrap-icons";
import { useUsers } from "../../api/user";
import { useInfoBoard } from "../../api/infoboard";
import HourglassGlobals, { HGContext } from "../../helpers/globals";
import { containsFuzzy } from "../../helpers/locale";
import { Permission } from "../../types/permission";
import { SearchItem, PERSON_ICON, DOCUMENT_ICON } from "../home/homeData";
import { congId } from "../home/homeShared";
import { searchableDestinations } from "../nav/navRegistry";

function useSearchIndex(): SearchItem[] {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const cid = congId(ctx);
  const usersQuery = useUsers();
  const infoQuery = useInfoBoard(cid, { enabled: !!cid });
  const delegateFor = ctx.globals.delegateFor;
  return useMemo(() => {
    const pages: SearchItem[] = searchableDestinations().map((d) => ({
      label: t(d.labelKey),
      kind: "page",
      area: t(d.searchAreaKey),
      icon: d.icon,
      to: d.path,
      external: d.external,
    }));
    // People — respect the same visibility rule as the former publisher search: a user who can
    // only see their group's reports is shown only their group's publishers (plus any delegates).
    const groupOnly =
      !!HourglassGlobals.authUser.group_id &&
      !HourglassGlobals.permissions.has(Permission.ViewReportsAll) &&
      HourglassGlobals.permissions.has(Permission.ViewReportsGroup);
    const delegateIds = new Set<number>(delegateFor?.map((du) => du.id));
    const visibleUsers = (usersQuery.data ?? []).filter((u) =>
      groupOnly ? u.group_id === HourglassGlobals.authUser.group_id || delegateIds.has(u.id) : true,
    );
    const people: SearchItem[] = visibleUsers.map((u) => ({
      label: u.displayName,
      kind: "person",
      area: "",
      icon: PERSON_ICON,
      to: `/user/${u.id}`,
    }));
    const docsArea = t("general.information-board");
    const docs: SearchItem[] = (infoQuery.data?.itemLinks ?? []).map((d) => ({
      label: d.title,
      kind: "document",
      area: docsArea,
      icon: DOCUMENT_ICON,
      to: d.url,
      external: true,
    }));
    return [...pages, ...people, ...docs];
  }, [t, usersQuery.data, delegateFor, infoQuery.data?.itemLinks]);
}

export function UnifiedSearch({
  variant = "hero",
  id,
  className,
}: {
  variant?: "hero" | "compact";
  id?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const index = useSearchIndex();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const s = q.trim();
    if (!s) return [];
    return index.filter((it) => containsFuzzy(it.label, s) || containsFuzzy(it.area, s)).slice(0, 8);
  }, [q, index]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  // Ctrl+/ focuses the search (kept from the former UserSearch shortcut). Only the persistent
  // nav (compact) instance claims the shortcut, so the hero on the home page doesn't fight it.
  useEffect(() => {
    if (variant !== "compact") return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "/") {
        e.preventDefault();
        e.stopPropagation();
        setQ("");
        inputRef.current?.focus();
      }
    };
    document.body.addEventListener("keydown", onKeyDown);
    return () => document.body.removeEventListener("keydown", onKeyDown);
  }, [variant]);

  const kindLabel = (k: SearchItem["kind"]) =>
    k === "page" ? t("general.pages") : k === "person" ? t("general.person") : t("nav.documents");

  const go = (item: SearchItem) => {
    setOpen(false);
    setQ("");
    if (item.external) window.open(item.to, "_blank", "noopener,noreferrer");
    else navigate(item.to);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Enter" && results[active]) {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <div className={"hp-search hp-search--" + variant + (className ? " " + className : "")} id={id} ref={boxRef}>
      <div className={"hp-search__field" + (open ? " is-open" : "")}>
        <SearchIcon className="hp-search__lead" size={variant === "compact" ? 15 : 18} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={t("search.hint")}
          aria-label={t("search.hint")}
        />
        {q && (
          <button className="hp-search__x" onClick={() => setQ("")} aria-label={t("general.clear")}>
            <XLg size={15} />
          </button>
        )}
        {variant === "compact" && !q && (
          <span className="hp-search__kbd d-none d-md-flex" aria-hidden="true">
            <kbd className="keyboard-button">Ctrl</kbd>+<kbd className="keyboard-button">/</kbd>
          </span>
        )}
      </div>

      {open && (
        <div className="hp-dropdown">
          {!q.trim() && (
            <div className="hp-hint">
              <div className="hp-hint__eyebrow">{t("home.search.hint.eyebrow")}</div>
              <div className="hp-hint__row">{t("home.search.hint.row")}</div>
            </div>
          )}
          {q.trim() && results.length === 0 && <div className="hp-noresult">{t("general.none")}</div>}
          {results.length > 0 && (
            <ul className="hp-reslist">
              {results.map((r, i) => {
                const Ico = r.icon;
                return (
                  <li key={r.kind + r.to + i}>
                    <div
                      className={"hp-res" + (i === active ? " is-active" : "")}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(r)}
                    >
                      <span className="hp-res__icon">
                        <Ico size={15} />
                      </span>
                      <span className="hp-res__label">{r.label}</span>
                      <span className="hp-res__area">{r.area}</span>
                      <span className="hp-res__kind">{kindLabel(r.kind)}</span>
                      <ArrowReturnLeft className="hp-res__enter" size={13} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
